import ServiceNameWithPromos from "../components/ServiceNameWithPromos";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  listFornitori,
  getCatalogoServizi,
  getFornitoreServizi,
  addFornitoreServizio,
  removeFornitoreServizio,
  adminGetFees,
  adminUpdateFees,
} from "../Admin/adminApi";
import FasceOrarie from "../Admin/FasceOrarie";
import FornitoreChiusure from "../components/FornitoreChiusure";
import ui from "./ServiziCatalogoShared.module.css";

// helper: leggi ruolo dal JWT in localStorage
function getRoleFromToken() {
  try {
    const t = localStorage.getItem("token");
    if (!t) return null;
    const base64 = t.split(".")[1];
    const json = JSON.parse(atob(base64.replace(/-/g, "+").replace(/_/g, "/")));
    return String(json?.role || json?.user?.role || "").toLowerCase() || null;
  } catch {
    return null;
  }
}

export default function AdminFornitoriServizi() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = Number(searchParams.get("tab"));
  const qFromUrl = searchParams.get("q") || "";
  const supplierFromUrl = searchParams.get("supplier") || "";

  const [fornitori, setFornitori] = useState([]);
  const [fornitoreId, setFornitoreId] = useState(supplierFromUrl);
  const [catalogo, setCatalogo] = useState([]);
  const [assoc, setAssoc] = useState([]);
  const [q, setQ] = useState(qFromUrl);

  const [tab, setTab] = useState(
    [0, 1, 2, 3].includes(tabFromUrl) ? tabFromUrl : 2
  );

  const [priceModal, setPriceModal] = useState({
    open: false,
    servizioId: null,
  });
  const [priceValue, setPriceValue] = useState("");
  const [leadTimeValue, setLeadTimeValue] = useState(""); // ore decimali
  const [logisticsFeeExempt, setLogisticsFeeExempt] = useState(false);
  const [logisticsFee, setLogisticsFee] = useState(2.0);

  const [sortBy, setSortBy] = useState(null); // 'descrizione' | 'prezzo' | 'lead'
  const [sortDir, setSortDir] = useState("asc");

  const currentSupplier = useMemo(
    () => fornitori.find((f) => String(f.id) === String(fornitoreId)),
    [fornitori, fornitoreId]
  );
  const supplierName =
    currentSupplier?.nome ||
    currentSupplier?.name ||
    (fornitoreId ? `Fornitore #${fornitoreId}` : "—");

  const minutesToHoursLabel = (min) => {
    const n = Number(min);
    if (!Number.isFinite(n)) return "—";
    const h = n / 60;
    return h.toLocaleString("it-IT", { maximumFractionDigits: 2 });
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const assocMap = useMemo(() => {
    const m = new Map();
    assoc.forEach((s) => m.set(s.servizio_id, s));
    return m;
  }, [assoc]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const filtered = catalogo.filter(
      (c) =>
        (!tab || c.tipo_id === tab) &&
        (!term ||
          (c.descrizione || "").toLowerCase().includes(term) ||
          (c.descrizione_estesa || "").toLowerCase().includes(term))
    );

    if (!sortBy) return filtered;

    return [...filtered].sort((a, b) => {
      let va, vb;

      if (sortBy === "descrizione") {
        va = a.descrizione || "";
        vb = b.descrizione || "";
      } else if (sortBy === "prezzo") {
        va = assocMap.get(a.id)?.prezzo ?? 0;
        vb = assocMap.get(b.id)?.prezzo ?? 0;
      } else if (sortBy === "lead") {
        va = assocMap.get(a.id)?.lead_time_min ?? Number.POSITIVE_INFINITY;
        vb = assocMap.get(b.id)?.lead_time_min ?? Number.POSITIVE_INFINITY;
      } else {
        va = 0;
        vb = 0;
      }

      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [catalogo, q, tab, sortBy, sortDir, assocMap]);

  useEffect(() => {
    (async () => {
      try {
        const [fs, cat] = await Promise.all([
          listFornitori(token),
          getCatalogoServizi(token),
        ]);

        setFornitori(fs || []);

        const normalizedCat = (Array.isArray(cat) ? cat : []).map((c) => ({
          id: Number(c.id),
          descrizione: c.descrizione,
          descrizione_estesa: c.descrizione_estesa,
          immagine: c.immagine,
          tipo_id: c.id_categoria_servizio,
        }));
        setCatalogo(normalizedCat);

        const initial = supplierFromUrl || (fs?.length ? String(fs[0].id) : "");
        setFornitoreId(initial);
      } catch (e) {
        console.error(e);
        toast.error("Errore caricamento fornitori o catalogo");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", String(tab));
    next.set("q", q);
    if (fornitoreId) next.set("supplier", String(fornitoreId));
    else next.delete("supplier");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q, fornitoreId]);

  useEffect(() => {
    setAssoc([]);
    if (!fornitoreId) return;
    let live = true;
    (async () => {
      try {
        const services = await getFornitoreServizi(fornitoreId, token);
        const norm = (Array.isArray(services) ? services : []).map((s) => ({
          servizio_id: Number(s.servizio_id ?? s.id_servizio ?? s.id),
          descrizione: s.descrizione,
          prezzo: s.prezzo,
          lead_time_min: s.lead_time_min ?? null,
          active_quantity_promos: s.active_quantity_promos || [],
        }));
        if (!live) return;
        setAssoc(norm);

        const f = await adminGetFees(fornitoreId, token);
        if (live && f?.logistics_fee != null) { setLogisticsFee(Number(f.logistics_fee)); setLogisticsFeeExempt(f.logistics_fee_exempt === true); }
      } catch (e) {
        console.error(e);
        toast.error("Errore caricamento servizi fornitore");
      }
    })();
    return () => { live = false; };
  }, [fornitoreId, token]);

  const saveAdminFee = async () => {
    const v = Math.max(2, Number(logisticsFee || 0));
    try {
      const r = await adminUpdateFees(fornitoreId, v, token, logisticsFeeExempt);
      setLogisticsFee(Number(r.logistics_fee));
      toast.success("Tariffa salvata");
    } catch (e) {
      toast.error(e.message || "Errore salvataggio tariffa");
    }
  };

  const openPriceModal = (servizioId, defaultPrice = "", defaultLead = "0") => {
    setPriceModal({ open: true, servizioId });
    setPriceValue(defaultPrice);
    setLeadTimeValue(defaultLead);
  };

  const closePriceModal = () => {
    setPriceModal({ open: false, servizioId: null });
    setPriceValue("");
    setLeadTimeValue("");
  };

  const confirmAssociate = async () => {
    const servizioId = priceModal.servizioId;
    const p = Number(String(priceValue).replace(",", "."));
    if (!Number.isFinite(p) || p <= 0) {
      toast.error("Prezzo non valido");
      return;
    }

    const ltRaw = String(leadTimeValue).trim().replace(",", ".");
    const hours = parseFloat(ltRaw);
    if (!Number.isFinite(hours) || hours < 0) {
      toast.error("Tempo di lavorazione non valido (>= 0 ore)");
      return;
    }
    const ltMinutes = Math.max(0, Math.round(hours * 60));

    try {
      const created = await addFornitoreServizio(
        fornitoreId,
        servizioId,
        p,
        token,
        { lead_time_min: ltMinutes }
      );

      const id = Number(created.servizio_id ?? created.id_servizio ?? servizioId);
      const descr =
        created.descrizione ||
        catalogo.find((c) => c.id === id)?.descrizione ||
        "Servizio";

      setAssoc((prev) => {
        const previous = prev.find(s => Number(s.servizio_id) === id);
        const next = prev.filter((s) => s.servizio_id !== id);
        next.push({
          servizio_id: id,
          descrizione: descr,
          active_quantity_promos: created.active_quantity_promos ?? previous?.active_quantity_promos ?? [],
          prezzo: created.prezzo ?? p,
          lead_time_min:
            created.lead_time_min != null ? created.lead_time_min : ltMinutes,
        });
        return next;
      });

      toast.success("Servizio salvato");
      closePriceModal();
    } catch (e) {
      console.error(e);
      toast.error("Errore salvataggio");
    }
  };

  const doDetach = async (servizioId) => {
    if (!window.confirm("Rimuovere il servizio da questo fornitore?")) return;
    try {
      await removeFornitoreServizio(fornitoreId, servizioId, token);
      setAssoc((prev) => prev.filter((s) => s.servizio_id !== servizioId));
      toast.success("Servizio rimosso");
    } catch (e) {
      console.error(e);
      toast.error("Errore rimozione");
    }
  };

  const doChangePrice = async (servizioId) => {
    const current = assocMap.get(servizioId);
    const defaultHours =
      current?.lead_time_min != null
        ? String(Math.round((Number(current.lead_time_min) / 60) * 100) / 100)
        : "0";
    openPriceModal(servizioId, String(current?.prezzo ?? ""), defaultHours);
  };

  const sortArrow = (col) =>
    sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div className={ui.page}>
      <header className={ui.header}>
        <div className={ui.headerLeft}>
          {isAdmin && (
            <button
              onClick={() => navigate(-1)}
              className={ui.backBtn}
              aria-label="Indietro"
              title="Indietro"
            >
              ‹
            </button>
          )}
          <div className={ui.titleBlock}>
            <h2 className={ui.hTitle}>Dettaglio di {supplierName}</h2>
            <p className={ui.hSub}>Tariffa, fasce orarie e catalogo servizi</p>
          </div>
        </div>
      </header>

      {/* Tariffa */}
      <div className={ui.card}>
        <div className={ui.cardHead}>
          <h3 className={ui.cardTitle}>Tariffa ritiro + consegna</h3>
        </div>
        <div className={ui.cardBody}>
          <div className={ui.feeGrid}>
            <div>
              <label className={ui.label}>Importo (min 2,00€)</label>
              <input
                type="number"
                min="2"
                step="0.10"
                value={logisticsFee}
                onChange={(e) => {
                  const raw = String(e.target.value).replace(",", ".");
                  const parsed = parseFloat(raw);
                  if (Number.isNaN(parsed)) setLogisticsFee(2);
                  else
                    setLogisticsFee(
                      Math.round(Math.max(2, parsed) * 100) / 100
                    );
                }}
                className={ui.input}
              />
              <div className={ui.hint}>Minimo 2,00€; l’esenzione azzera l’addebito mantenendo la tariffa per la successiva riattivazione.</div>
              <label><input type="checkbox" checked={logisticsFeeExempt} onChange={e => setLogisticsFeeExempt(e.target.checked)} /> Esenzione trasporto per questo fornitore</label>
            </div>
            <button onClick={saveAdminFee} className={ui.btnPrimary}>
              Salva
            </button>
          </div>
        </div>
      </div>

      {/* Fasce orarie */}
      {fornitoreId && (
        <div className={ui.card}>
          <div className={ui.cardHead}>
            <h3 className={ui.cardTitle}>Fasce orarie</h3>
          </div>
          <div className={ui.cardBody}>
            <FasceOrarie idFornitore={Number(fornitoreId)} title=" " />
          </div>
        </div>
      )}

      {/* Chiusure */}
      {fornitoreId && (
        <div className={ui.card}>
          <div className={ui.cardHead}>
            <h3 className={ui.cardTitle}>Periodi di chiusura</h3>
          </div>
          <div className={ui.cardBody}>
            <FornitoreChiusure
              token={token}
              ui={ui}
              mode="admin"
              fornitoreId={Number(fornitoreId)}
              title="Chiusure del fornitore"
            />
          </div>
        </div>
      )}

      {/* Catalogo */}
      <div className={ui.card}>
        <div className={ui.cardHead}>
          <div className={ui.actionRow}>
            <div
              className={ui.pills}
              role="tablist"
              aria-label="Categoria servizi"
            >
              <button
                className={`${ui.pill} ${tab === 0 ? ui.pillActive : ""}`}
                onClick={() => setTab(0)}
                role="tab"
                aria-selected={tab === 0}
              >
                Tutti
              </button>
              <button
                className={`${ui.pill} ${tab === 1 ? ui.pillActive : ""}`}
                onClick={() => setTab(1)}
                role="tab"
                aria-selected={tab === 1}
              >
                Stiratura
              </button>
              <button
                className={`${ui.pill} ${tab === 2 ? ui.pillActive : ""}`}
                onClick={() => setTab(2)}
                role="tab"
                aria-selected={tab === 2}
              >
                Lavaggio
              </button>
              <button
                className={`${ui.pill} ${tab === 3 ? ui.pillActive : ""}`}
                onClick={() => setTab(3)}
                role="tab"
                aria-selected={tab === 3}
              >
                Entrambi
              </button>
            </div>

            <div className={ui.actionRight}>
              <input
                className={`${ui.input} ${ui.search}`}
                placeholder="Cerca nel catalogo…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={ui.cardBody}>
          <div className={ui.tableWrap}>
            <table className={`${ui.table} ${ui.adminSupplierTable}`}>
              <thead>
                <tr>
                  <th
                    className={ui.th}
                    onClick={() => handleSort("descrizione")}
                  >
                    Servizio{sortArrow("descrizione")}
                  </th>
                  <th className={ui.th} onClick={() => handleSort("prezzo")}>
                    Prezzo{sortArrow("prezzo")}
                  </th>
                  <th className={ui.th} onClick={() => handleSort("lead")}>
                    Tempo lav. (Ore){sortArrow("lead")}
                  </th>
                  <th className={ui.th} style={{ cursor: "default" }}>
                    Azioni
                  </th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => {
                  const isOn = assocMap.has(row.id);
                  const mine = assocMap.get(row.id);

                  return (
                    <tr key={row.id} className={isOn ? ui.rowOn : ""}>
                      <td className={ui.td}>
                        <ServiceNameWithPromos name={row.descrizione} className={ui.serviceName} promos={mine?.active_quantity_promos} />
                      </td>

                      <td className={`${ui.td} ${ui.num}`}>
                        {isOn
                          ? `€ ${Number(mine?.prezzo ?? 0).toFixed(2)}`
                          : "—"}
                      </td>

                      <td className={`${ui.td} ${ui.num}`}>
                        {isOn && mine?.lead_time_min != null
                          ? `${minutesToHoursLabel(mine.lead_time_min)} h`
                          : "—"}
                      </td>

                      <td className={ui.td}>
                        <div className={ui.actions}>
                          {isOn ? (
                            <>
                              <button
                                onClick={() => doChangePrice(row.id)}
                                className={ui.btnSecondary}
                              >
                                Modifica
                              </button>
                              <button
                                onClick={() => doDetach(row.id)}
                                className={ui.btnDanger}
                              >
                                Rimuovi
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => openPriceModal(row.id, "", "0")}
                              className={ui.btnPrimary}
                            >
                              Associa
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal prezzo+tempo */}
      {priceModal.open && (
        <div className={ui.modalBackdrop} onClick={closePriceModal}>
          <div className={ui.modal} onClick={(e) => e.stopPropagation()}>
            <div className={ui.modalHead}>
              <h3 className={ui.modalTitle}>Imposta prezzo e tempo</h3>
              <button onClick={closePriceModal} className={ui.modalClose}>
                ✕
              </button>
            </div>

            <div className={ui.modalBody}>
              <div>
                <label className={ui.label}>Prezzo (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceValue}
                  onChange={(e) => setPriceValue(e.target.value)}
                  placeholder="€ 10.00"
                  className={ui.input}
                />
              </div>

              <div>
                <label className={ui.label}>Tempo di lavorazione (ore)</label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  required
                  value={leadTimeValue}
                  onChange={(e) => setLeadTimeValue(e.target.value)}
                  placeholder="es. 1.5 = 1h30m"
                  className={ui.input}
                />
                <div className={ui.hint}>
                  Ore anche decimali. Verranno salvate in minuti.
                </div>
              </div>
            </div>

            <div className={ui.modalActions}>
              <button onClick={closePriceModal} className={ui.btnSecondary}>
                Annulla
              </button>
              <button onClick={confirmAssociate} className={ui.btnPrimary}>
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
